import moment from 'moment';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import omniLogo from '../assets/images/omni.webp';

// Statement export for the admin Transaction N listing.
// Field names follow the same fallback chains the table columns use, since the
// partner feed is not consistent about casing/naming across records.

const OMNI_RED = [238, 49, 36];
const OMNI_DARK = [35, 24, 21];
const GREY = [102, 102, 102];
const HEAD_BG = [243, 244, 246];
const BORDER = [213, 215, 219];

const pick = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return '';
};

const num = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== '' && !isNaN(Number(v))) return Number(v);
  }
  return null;
};

const LOGO_W = 104;
const LOGO_H = Math.round((LOGO_W * 160 / 620) * 10) / 10; // keep the 620x160 aspect

const loadLogoPng = () =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        try {
          // Rasterise at 3x the placed size — plenty for print, and a fraction of
          // the file size of embedding the full-resolution asset.
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(LOGO_W * 3);
          canvas.height = Math.round(LOGO_H * 3);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/png'));
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = omniLogo;
    } catch (e) {
      resolve(null);
    }
  });

const fmtAmount = (n) =>
  n == null ? 'N/A' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateTime = (d) => (d && moment(d).isValid() ? moment(d).local().format('YYYY-MM-DD HH:mm:ss') : '');

// One normalised record per transaction — the single source both exports read.
export const toStatementRow = (r) => {
  const created = pick(r.transaction_date, r.txn_date, r.transactionDate, r.createdAt, r.dateTime);
  return {
    createdAt: fmtDateTime(created),
    type: (pick(r.type, r.txn_type, r.transaction_type) || '-').toString().toUpperCase(),
    vrn: pick(r.vehicle_number, r.vehicle_no, r.vrn) || 'N/A',
    amount: num(r.amount, r.debit_amount, r.txn_amount),
    status: (pick(r.status, r.txn_status, r.state) || 'N/A').toString().toUpperCase(),
    merchant: pick(r.merchant, r.plaza_name, r.toll_plaza, r.plazaName, r.description) || '-',
    // Names can arrive as *_name, camelCase, a nested object, or a plain string —
    // cover every shape so the column always reflects whatever the API sends.
    distributor: pick(
      r.distributor_name, r.distributorName,
      r.distributor?.name, r.distributor?.username,
      typeof r.distributor === 'string' ? r.distributor : null,
    ) || '-',
    dealer: pick(
      r.dealer_name, r.dealerName,
      r.dealer?.name, r.dealer?.username,
      typeof r.dealer === 'string' ? r.dealer : null,
    ) || '-',
    agent: pick(
      r.agent_name, r.agentName,
      r.agent?.name, r.agent?.username,
      typeof r.agent === 'string' ? r.agent : null,
    ) || '-',
  };
};

// Column order mirrors the reference statement, with the org columns this view
// carries appended. The org columns marked with `org` follow the same role rule
// as the page filters: you do not get a column for your own level or above.
const COLUMNS = [
  { key: 'createdAt', header: 'TRANSACTION CREATION DATE' },
  { key: 'type', header: 'TXN TYPE' },
  { key: 'vrn', header: 'VRN' },
  { key: 'amount', header: 'AMOUNT', numeric: true },
  { key: 'status', header: 'STATUS' },
  { key: 'merchant', header: 'MERCHANT/TOLL NAME' },
  { key: 'distributor', header: 'DISTRIBUTOR NAME', org: 'distributor' },
  { key: 'dealer', header: 'DEALER NAME', org: 'dealer' },
  { key: 'agent', header: 'AGENT NAME', org: 'agent' },
];

const COL_WIDTH = {
  createdAt: 22, type: 10, vrn: 14,
  amount: 12, status: 12, merchant: 30, distributor: 20, dealer: 20, agent: 20,
};

// show: { distributor, dealer, agent } — omitted means visible (admin).
const visibleColumns = (show) => COLUMNS.filter((c) => !c.org || show?.[c.org] !== false);

const summarise = (rows) => {
  let credit = 0;
  let debit = 0;
  rows.forEach((r) => {
    if (r.amount == null) return;
    if (r.type === 'CREDIT') credit += r.amount;
    else if (r.type === 'DEBIT') debit += r.amount;
  });
  return { count: rows.length, credit, debit, net: credit - debit };
};

const periodLabel = (from, to) => {
  const f = from ? moment(from).format('YYYY-MM-DD') : '';
  const t = to ? moment(to).format('YYYY-MM-DD') : '';
  if (!f && !t) return 'All transactions';
  // When "To" isn't picked the range runs up to today — show today's ACTUAL date
  // (matching the default the backend uses) instead of the literal word "Today".
  const tShown = t || moment().format('YYYY-MM-DD');
  return `From : ${f || 'Beginning'} To : ${tShown}`;
};

// When the vehicle filter is applied the number leads the file name, matching
// the reference statement (RJ52GB1501_statement_2026-08-07_2026-08-25.pdf).
export const statementFileName = (meta = {}, ext) => {
  const f = meta.from ? moment(meta.from).format('YYYY-MM-DD') : 'all';
  const t = meta.to ? moment(meta.to).format('YYYY-MM-DD') : 'all';
  const vehicle = (meta.vehicle || '').toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const prefix = vehicle || 'transactions';
  return `${prefix}_statement_${f}_${t}.${ext}`;
};

// meta: { from, to, vehicle, distributor, dealer, agent }
// The sheet holds the transaction rows only — no title/filter/summary block —
// so it can be sorted, filtered and pivoted straight away.
export const downloadStatementExcel = (records, meta = {}) => {
  const rows = records.map(toStatementRow);
  const cols = visibleColumns(meta.show);

  const aoa = [
    cols.map((c) => c.header),
    ...rows.map((r) => cols.map((c) => r[c.key])),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map((c) => ({ wch: COL_WIDTH[c.key] || 16 }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Statement');
  XLSX.writeFile(wb, statementFileName(meta, 'xlsx'));
};

export const downloadStatementPdf = async (records, meta = {}) => {
  const rows = records.map(toStatementRow);
  const cols = visibleColumns(meta.show);
  const generated = moment().format('YYYY-MM-DD HH:mm:ss');

  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 34;

  // ---- Header band: app logo, statement title, timestamp ----
  const logo = await loadLogoPng();
  if (logo) {
    doc.addImage(logo, 'PNG', M, 24, LOGO_W, LOGO_H);
  } else {
    // Fall back to the wordmark if the asset could not be decoded.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...OMNI_RED);
    doc.text('Omni', M, 40);
    const omniW = doc.getTextWidth('Omni');
    doc.setTextColor(...OMNI_DARK);
    doc.text('Card', M + omniW, 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.text('business payments.ai', M, 49);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('FASTag Statement', pageW - M, 40, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GREY);
  doc.text(`Generated ${generated}`, pageW - M, 51, { align: 'right' });

  doc.setDrawColor(...OMNI_DARK);
  doc.setLineWidth(1.2);
  doc.line(M, 60, pageW - M, 60);

  // ---- Filter details (this listing spans many vehicles, so the reference's
  // per-customer block is replaced by the filters the export was taken under) --
  let y = 78;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...OMNI_DARK);
  doc.text('FILTER DETAILS', M, y);

  const details = [
    ['Vehicle Number:', meta.vehicle || 'All Vehicles'],
    meta.show?.distributor !== false && ['Distributor:', meta.distributor || 'All Distributors'],
    meta.show?.dealer !== false && ['Dealer:', meta.dealer || 'All Dealers'],
    meta.show?.agent !== false && ['Agent:', meta.agent || 'All Agents'],
    ['Period:', periodLabel(meta.from, meta.to)],
  ].filter(Boolean);
  doc.setFont('helvetica', 'normal');
  details.forEach(([label, value], i) => {
    const ly = y + 12 + i * 12;
    doc.setTextColor(...GREY);
    doc.text(label, M, ly);
    doc.setTextColor(...OMNI_DARK);
    doc.text(String(value), M + 90, ly);
  });
  y = y + 12 + details.length * 12 + 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(`Statement for the period (${periodLabel(meta.from, meta.to)})`, M, y);

  // ---- Transactions ----
  // Date/time stacked on two lines, matching the reference statement.
  const cellText = (r, key) => {
    if (key === 'createdAt') return r.createdAt ? r.createdAt.replace(' ', '\n') : 'N/A';
    if (key === 'amount') return fmtAmount(r.amount);
    return r[key];
  };
  const body = rows.map((r) => cols.map((c) => cellText(r, c.key)));

  // Styles are keyed off the visible positions, which shift when org columns drop.
  const columnStyles = {};
  cols.forEach((c, i) => {
    if (c.numeric) columnStyles[i] = { ...(columnStyles[i] || {}), halign: 'right' };
  });

  autoTable(doc, {
    startY: y + 8,
    margin: { left: M, right: M, bottom: 34 },
    head: [cols.map((c) => c.header)],
    body,
    theme: 'grid',
    styles: {
      fontSize: 6.8,
      cellPadding: 3,
      lineColor: BORDER,
      lineWidth: 0.5,
      textColor: OMNI_DARK,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: { fillColor: HEAD_BG, textColor: OMNI_DARK, fontStyle: 'bold', fontSize: 6.2 },
    // Widths are left to autoTable: fixed ones overflowed the page once the
    // 24-char tag id and long plaza/dealer names were taken into account.
    columnStyles,
    didDrawPage: () => {
      const page = doc.internal.getNumberOfPages();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...GREY);
      doc.text(`Page ${page}`, pageW - M, pageH - 18, { align: 'right' });
      doc.text('OmniCard — FASTag Statement', M, pageH - 18);
    },
  });

  doc.save(statementFileName(meta, 'pdf'));
};
